/**
 * GO Commerce Cache Configuration Store
 * 
 * Manages cache configuration settings for the API caching system
 * allowing dynamic TTL adjustments and refresh tracking.
 */
import { defineStore } from 'pinia';

interface CacheTTLConfig {
  products: number;
  networkBalance: number;
  currentServices: number;
  userProfile: number;
  recharges: number;
  [key: string]: number; // Allow dynamic additions
}

export const useCacheConfigStore = defineStore('cacheConfig', {
  state: () => ({
    ttlConfig: {
      products: 60 * 60 * 1000, // 1 hour
      networkBalance: 5 * 60 * 1000, // 5 minutes
      currentServices: 15 * 60 * 1000, // 15 minutes
      userProfile: 30 * 60 * 1000, // 30 minutes
      recharges: 10 * 60 * 1000, // 10 minutes
    } as CacheTTLConfig,
    
    // Track active refreshes to prevent duplicate refreshes
    activeRefreshes: {} as Record<string, boolean>,
    
    // Flag to enable/disable entire caching system (emergency toggle)
    enabled: true
  }),
  
  getters: {
    isCacheEnabled(): boolean {
      return this.enabled;
    }
  },
  
  actions: {
    // Update TTL for a specific endpoint
    updateTTL(endpoint: string, ttlMs: number) {
      this.ttlConfig[endpoint] = ttlMs;
      console.log(`[CacheConfig] Updated TTL for ${endpoint} to ${ttlMs}ms`);
    },
    
    // Get current TTL for an endpoint
    getTTL(endpoint: string): number {
      return this.ttlConfig[endpoint] || 60 * 60 * 1000; // Default 1 hour
    },
    
    // Mark refresh as active
    markRefreshActive(endpoint: string) {
      this.activeRefreshes[endpoint] = true;
    },
    
    // Mark refresh as completed
    markRefreshComplete(endpoint: string) {
      this.activeRefreshes[endpoint] = false;
    },
    
    // Check if endpoint is currently refreshing
    isRefreshing(endpoint: string): boolean {
      return !!this.activeRefreshes[endpoint];
    },
    
    // Disable caching (emergency toggle)
    disableCache() {
      this.enabled = false;
      console.warn('[CacheConfig] API caching system has been DISABLED');
    },
    
    // Enable caching
    enableCache() {
      this.enabled = true;
      console.log('[CacheConfig] API caching system has been ENABLED');
    }
  }
});
